import { z } from "zod";

const MessageSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("handshake.init"),
    appId: z.string(),
    token: z.string(),
  }),
  z.object({
    type: z.literal("api.request"),
    nonce: z.string(),
    seq: z.number().int().positive(),
    requestId: z.string().max(50),
    document: z.string().max(10240),
    variables: z.string().max(51200).optional(),
  }),
  z.object({
    type: z.literal("ui.navigate"),
    nonce: z.string(),
    seq: z.number().int().positive(),
    path: z.string().regex(/^\/[a-zA-Z0-9\-_/]*$/),
  }),
  z.object({
    type: z.literal("ui.toast"),
    nonce: z.string(),
    seq: z.number().int().positive(),
    title: z.string().max(100),
    message: z.string().max(300),
    color: z.enum(["green", "red", "yellow", "blue"]),
  }),
  z.object({
    type: z.literal("ui.resize"),
    nonce: z.string(),
    seq: z.number().int().positive(),
    height: z.number().int().min(100).max(2000),
  }),
  z.object({
    type: z.literal("ui.confirm"),
    nonce: z.string(),
    seq: z.number().int().positive(),
    requestId: z.string().max(50),
    title: z.string().max(100),
    message: z.string().max(500),
  }),
  z.object({
    type: z.literal("metafield.get"),
    nonce: z.string(),
    seq: z.number().int().positive(),
    requestId: z.string().max(50),
    namespace: z
      .string()
      .max(50)
      .regex(/^[a-zA-Z0-9_-]+$/),
    entityType: z.string().max(50),
    entityId: z.string().max(50),
    key: z.string().max(100),
  }),
  z.object({
    type: z.literal("metafield.set"),
    nonce: z.string(),
    seq: z.number().int().positive(),
    requestId: z.string().max(50),
    namespace: z
      .string()
      .max(50)
      .regex(/^[a-zA-Z0-9_-]+$/),
    entityType: z.string().max(50),
    entityId: z.string().max(50),
    key: z.string().max(100),
    value: z.string().max(65536),
  }),
]);

type BridgeMessage = z.infer<typeof MessageSchema>;

export interface AppBridgeConfig {
  appId: string;
  expectedOrigin: string;
  handshakeToken: string;
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  grantedPermissions: string[];
  manifestNamespaces: string[];
  contextData: Record<string, unknown>;
  moduleToken?: string;
  apiBaseUrl?: string;
  marketplaceAppId?: string;
  onApiRequest: (
    requestId: string,
    document: string,
    variables?: string,
  ) => void;
  onNavigate: (path: string) => void;
  onToast: (title: string, message: string, color: string) => void;
  onResize: (height: number) => void;
  onConfirm: (requestId: string, title: string, message: string) => void;
  onMetafieldGet: (
    requestId: string,
    namespace: string,
    entityType: string,
    entityId: string,
    key: string,
  ) => void;
  onMetafieldSet: (
    requestId: string,
    namespace: string,
    entityType: string,
    entityId: string,
    key: string,
    value: string,
  ) => void;
  onSecurityViolation: (type: string, details: string) => void;
}

const RATE_LIMIT_WINDOW_MS = 1000;
const RATE_LIMIT_MAX = 60;

export class AppBridge {
  private readonly config: AppBridgeConfig;
  private readonly sessionNonce: string;
  private lastReceivedSeq: number;
  private readonly messageTimestamps: number[];
  private handshakeComplete: boolean;

  constructor(config: AppBridgeConfig) {
    this.config = config;
    this.sessionNonce = crypto.randomUUID();
    this.lastReceivedSeq = 0;
    this.messageTimestamps = [];
    this.handshakeComplete = false;
  }

  start(): void {
    window.addEventListener("message", this.handleMessage);
  }

  destroy(): void {
    window.removeEventListener("message", this.handleMessage);
  }

  sendMessage(data: unknown): void {
    this.config.iframeRef.current?.contentWindow?.postMessage(
      data,
      this.config.expectedOrigin,
    );
  }

  sendResponse(requestId: string, data: unknown): void {
    this.sendMessage({ type: "response", requestId, data });
  }

  sendError(requestId: string, error: string): void {
    this.sendMessage({ type: "error", requestId, error });
  }

  private isRateLimited(): boolean {
    const now = Date.now();
    const windowStart = now - RATE_LIMIT_WINDOW_MS;

    // Remove timestamps outside the window
    while (
      this.messageTimestamps.length > 0 &&
      this.messageTimestamps[0] < windowStart
    ) {
      this.messageTimestamps.shift();
    }

    if (this.messageTimestamps.length >= RATE_LIMIT_MAX) {
      return true;
    }

    this.messageTimestamps.push(now);
    return false;
  }

  private handleMessage = (event: MessageEvent): void => {
    // 1. Validate origin
    // With sandbox="allow-scripts allow-forms allow-same-origin", the iframe
    // preserves its real origin — no need to accept opaque "null" origins.
    if (event.origin !== this.config.expectedOrigin) {
      this.config.onSecurityViolation(
        "origin_mismatch",
        `Expected ${this.config.expectedOrigin}, got ${event.origin}`,
      );
      return;
    }

    // 2. Validate source
    if (event.source !== this.config.iframeRef.current?.contentWindow) {
      this.config.onSecurityViolation(
        "source_mismatch",
        "Message source does not match iframe contentWindow",
      );
      return;
    }

    const rawData = event.data;

    // 3. Handle handshake.init
    if (rawData?.type === "handshake.init") {
      if (rawData.token !== this.config.handshakeToken) {
        this.config.onSecurityViolation(
          "handshake_token_mismatch",
          "Handshake token does not match expected value",
        );
        return;
      }

      if (rawData.appId !== this.config.appId) {
        this.config.onSecurityViolation(
          "appId_mismatch",
          `Expected ${this.config.appId}, got ${rawData.appId}`,
        );
        return;
      }

      this.handshakeComplete = true;
      this.sendMessage({
        type: "handshake.ack",
        nonce: this.sessionNonce,
        context: this.config.contextData,
        theme: "dark",
        locale: "en",
        moduleToken: this.config.moduleToken,
        apiBaseUrl: this.config.apiBaseUrl,
        marketplaceAppId: this.config.marketplaceAppId,
        grantedPermissions: this.config.grantedPermissions,
      });
      return;
    }

    // 4. Parse and validate with Zod
    const parseResult = MessageSchema.safeParse(rawData);
    if (!parseResult.success) {
      this.config.onSecurityViolation(
        "invalid_message",
        `Schema validation failed: ${parseResult.error.message}`,
      );
      return;
    }

    const message = parseResult.data;

    // Handshake.init is already handled above; all remaining types have nonce/seq
    if (message.type === "handshake.init") {
      return;
    }

    // 5. Validate nonce
    if (message.nonce !== this.sessionNonce) {
      this.config.onSecurityViolation(
        "nonce_mismatch",
        `Expected ${this.sessionNonce}, got ${message.nonce}`,
      );
      return;
    }

    // 6. Validate sequence number
    if (message.seq <= this.lastReceivedSeq) {
      this.config.onSecurityViolation(
        "seq_replay",
        `Expected seq > ${this.lastReceivedSeq}, got ${message.seq}`,
      );
      return;
    }
    this.lastReceivedSeq = message.seq;

    // 7. Rate limit check
    if (this.isRateLimited()) {
      this.config.onSecurityViolation(
        "rate_limited",
        `Exceeded ${RATE_LIMIT_MAX} messages per second`,
      );
      return;
    }

    // 8. Route to appropriate callback
    this.routeMessage(message);
  };

  private routeMessage(
    message: Exclude<BridgeMessage, { type: "handshake.init" }>,
  ): void {
    switch (message.type) {
      case "api.request":
        this.config.onApiRequest(
          message.requestId,
          message.document,
          message.variables,
        );
        break;

      case "ui.navigate":
        this.config.onNavigate(message.path);
        break;

      case "ui.toast":
        this.config.onToast(message.title, message.message, message.color);
        break;

      case "ui.resize":
        this.config.onResize(message.height);
        break;

      case "ui.confirm":
        this.config.onConfirm(
          message.requestId,
          message.title,
          message.message,
        );
        break;

      case "metafield.get":
        if (!this.config.manifestNamespaces.includes(message.namespace)) {
          this.config.onSecurityViolation(
            "namespace_unauthorized",
            `Namespace "${message.namespace}" not in manifest`,
          );
          return;
        }
        this.config.onMetafieldGet(
          message.requestId,
          message.namespace,
          message.entityType,
          message.entityId,
          message.key,
        );
        break;

      case "metafield.set":
        if (!this.config.manifestNamespaces.includes(message.namespace)) {
          this.config.onSecurityViolation(
            "namespace_unauthorized",
            `Namespace "${message.namespace}" not in manifest`,
          );
          return;
        }
        this.config.onMetafieldSet(
          message.requestId,
          message.namespace,
          message.entityType,
          message.entityId,
          message.key,
          message.value,
        );
        break;
    }
  }
}
